/**
 * MapOnboardingHint — small overlay shown on top of the empty landing map.
 *
 * Triggered when:
 *   • The map renders without any active layer (default state on the
 *     marketing landing since we disabled demoMode).
 *   • The user has not dismissed the hint before (persisted via
 *     localStorage so we don't pester returning visitors).
 *
 * Auto-dismisses when:
 *   • The user opens the layers panel (they obviously got the message).
 *   • The user clicks the CTA or the close button.
 *
 * Positioned top-center, non-blocking (pointer-events: auto only on the
 * card itself), so the user can still pan/zoom the underlying map.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, X } from 'lucide-react';

const LS_KEY = 'dronemaps_onboarding_dismissed';

export default function MapOnboardingHint({ visible, onDismiss, onCtaClick }) {
  const { t } = useTranslation();

  // Once dismissed, never show again in this browser.
  const persistDismiss = () => {
    try { localStorage.setItem(LS_KEY, '1'); } catch { /* private mode */ }
    onDismiss?.();
  };

  // Auto-dismiss permanently the first time the layers panel opens — the
  // user has clearly discovered the feature.
  useEffect(() => {
    if (!visible) {
      try { localStorage.setItem(LS_KEY, '1'); } catch { /* private mode */ }
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none"
      data-testid="map-onboarding-hint"
    >
      <div className="pointer-events-auto bg-white/95 backdrop-blur border border-slate-200 rounded-xl shadow-lg px-4 py-3 max-w-md flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-sky-100 text-sky-600 inline-flex items-center justify-center">
          <Layers className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 leading-tight">
            {t('mapOnboarding.title', 'Sélectionnez les zones à afficher')}
          </div>
          <p className="text-xs text-slate-600 leading-relaxed mt-1">
            {t('mapOnboarding.desc', 'La carte est vierge par défaut. Activez les couches pour voir les zones réglementaires de votre pays.')}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { persistDismiss(); onCtaClick?.(); }}
              className="inline-flex items-center gap-1.5 px-3 h-7 rounded-md bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium"
              data-testid="map-onboarding-cta"
            >
              <Layers className="w-3.5 h-3.5" />
              {t('mapOnboarding.cta', 'Ouvrir les couches')}
            </button>
            <button
              type="button"
              onClick={persistDismiss}
              className="px-2 h-7 text-xs text-slate-500 hover:text-slate-700 rounded-md hover:bg-slate-100"
              data-testid="map-onboarding-dismiss"
            >
              {t('mapOnboarding.dismiss', 'Ignorer')}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={persistDismiss}
          className="shrink-0 p-1 -m-1 text-slate-400 hover:text-slate-600 rounded"
          aria-label="Close hint"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export const hasDismissedOnboarding = () => {
  try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
};
